/**
 * معايير الاستحقاق — كمية مستحقة يومياً لكل (مديرية × صنف)، مستقلة تماماً
 * عن الأعداد العامة. الأدمن يُدخل الرقم النهائي مباشرة — النظام لا يحاول
 * فهم قواعد الاستحقاق (من يُصرف له كم وجبة، نسبة الماء...)، فقط يقارن
 * لاحقاً ما طُلب فعلاً بهذا الرقم.
 *
 * زر "🧮 اقترح" اختياري: يقترح رقماً من نسبة الفرد المضبوطة على الصنف
 * (في القواميس) × عدد الأفراد — للأصناف ذات النسبة الثابتة كالماء والخبز فقط.
 */
import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { useActiveItems } from "../../hooks/useItems";
import ExportButton from "../../components/shared/ExportButton";
import { downloadApiFile } from "../../api/download";
import PrintLayout from "../../components/Layout/PrintLayout";
import { formatDate, formatDateLong, formatNumber } from "../../lib/date";
import type { ConsumptionNormAsOf, Directorate } from "../../types";

type Base = "full" | "remaining";
type CellKey = `${number}-${number}`;

export default function ConsumptionNormsTab({ asOf, govId }: { asOf: string; govId: string }) {
  const { isRole } = useAuth();
  const qc = useQueryClient();
  const canEdit = isRole("SYSTEM_ADMIN");

  const [base,   setBase]   = useState<Base>("full");
  const [active, setActive] = useState<{ dirId: number; itemId: number } | null>(null);
  const [value,  setValue]  = useState("");
  const [error,  setError]  = useState("");
  const [suggesting, setSuggesting] = useState<CellKey | null>(null);

  const { data: items = [] } = useActiveItems();
  const { data: dirs = [] } = useQuery({
    queryKey: ["directorates", "active"],
    queryFn: () => api.get<{ data: Directorate[] }>("/directorates?isActive=true").then((r) => r.data.data),
  });
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["norms-as-of", asOf],
    enabled: !!asOf,
    queryFn: () => api.get<{ data: ConsumptionNormAsOf[] }>(`/consumption-norms/as-of?asOf=${asOf}`).then((r) => r.data.data),
  });

  const byKey = useMemo(() => {
    const m = new Map<CellKey, ConsumptionNormAsOf>();
    for (const r of rows) m.set(`${r.directorateId}-${r.itemId}`, r);
    return m;
  }, [rows]);

  const visible = govId ? dirs.filter((d) => d.governorateId === Number(govId)) : dirs;

  const open = (dirId: number, itemId: number) => {
    if (!canEdit) return;
    setActive({ dirId, itemId });
    setValue(String(byKey.get(`${dirId}-${itemId}`)?.targetQty ?? 0));
    setError("");
  };

  const save = async () => {
    if (!active) return;
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) { setError("أدخل رقماً صحيحاً ≥ 0"); return; }

    const existing = byKey.get(`${active.dirId}-${active.itemId}`);
    if (existing && existing.targetQty === num && existing.effectiveFrom === asOf) {
      setActive(null); return;
    }

    try {
      if (existing && existing.effectiveFrom === asOf) {
        await api.put(`/consumption-norms/${existing.id}`, { targetQty: num });
      } else {
        await api.post("/consumption-norms", {
          effectiveFrom: asOf, directorateId: active.dirId, itemId: active.itemId, targetQty: num,
        });
      }
      await qc.invalidateQueries({ queryKey: ["norms-as-of"] });
      setActive(null);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "حدث خطأ أثناء الحفظ"
      );
    }
  };

  const suggest = async (dirId: number, itemId: number) => {
    const key: CellKey = `${dirId}-${itemId}`;
    setSuggesting(key); setError("");
    try {
      const res = await api.get<{ data: { suggested: number | null; reason?: string } }>(
        `/consumption-norms/suggest?directorateId=${dirId}&itemId=${itemId}&base=${base}&asOf=${asOf}`
      );
      const { suggested, reason } = res.data.data;
      if (suggested == null) { setError(reason ?? "تعذّر اقتراح رقم لهذا الصنف"); return; }
      setActive({ dirId, itemId });
      setValue(String(suggested));
    } catch {
      setError("تعذّر جلب الاقتراح");
    } finally {
      setSuggesting(null);
    }
  };

  const itemsWithRatio = useMemo(() => new Set(items.filter((i) => i.perPersonRatio != null).map((i) => i.id)), [items]);

  const cell = (d: Directorate, itemId: number) => {
    const key: CellKey = `${d.id}-${itemId}`;
    const r = byKey.get(key);
    const isActive = active?.dirId === d.id && active.itemId === itemId;

    if (isActive) {
      return (
        <input type="number" min="0" step="any" autoFocus dir="ltr"
          className="w-20 text-center text-sm border-2 border-primary-700 rounded px-1 py-1 focus:outline-none"
          value={value} onChange={(e) => setValue(e.target.value)}
          onBlur={() => void save()}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); void save(); }
            if (e.key === "Escape") { setActive(null); setError(""); }
          }} />
      );
    }
    return (
      <div className="flex items-center justify-center gap-1">
        <span dir="ltr" onClick={() => open(d.id, itemId)}
          className={`inline-block min-w-[3rem] px-2 py-1 rounded ${canEdit ? "cursor-pointer hover:bg-primary-50" : ""} ${r ? "" : "text-sand-300"}`}>
          {r ? formatNumber(r.targetQty, 2) : "—"}
        </span>
        {canEdit && itemsWithRatio.has(itemId) && (
          <button type="button" title="اقترح رقماً من نسبة الفرد"
            className="text-[11px] text-primary-600 hover:text-primary-800 disabled:opacity-40"
            disabled={suggesting === key} onClick={() => void suggest(d.id, itemId)}>
            {suggesting === key ? "..." : "🧮"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3 no-print">
        <div>
          <label className="label">أساس الاقتراح (🧮)</label>
          <select className="input w-56" value={base} onChange={(e) => setBase(e.target.value as Base)}>
            <option value="full">العدد الكامل</option>
            <option value="remaining">الباقي (بعد استبعاد المبدل)</option>
          </select>
        </div>
        <ExportButton onExport={() => void downloadApiFile(`/consumption-norms/as-of?asOf=${asOf}&format=csv`)} />
      </div>

      {error && <p className="text-sm text-red-700 font-medium mb-3">{error}</p>}

      <div className="alert-info mb-4 no-print text-xs leading-relaxed">
        <strong>ما هذا التبويب؟</strong> الكمية المستحقة يومياً لكل صنف لكل مديرية — تُدخَل مباشرة
        من واقع كل مديرية (من يُصرف له كم وجبة، وأي هامش للماء أو الخبز)، لا يحسبها النظام تلقائياً.
        هذا الرقم هو ما تُقارَن به الطلبات الفعلية في لوحة المعلومات، لكل صنف على حدة.
        {canEdit && (
          <>
            <br />
            زر 🧮 يظهر فقط للأصناف التي ضبطتَ لها "نسبة استحقاق الفرد" من القواميس ← الأصناف،
            ويقترح رقماً قابلاً للتعديل قبل الحفظ — لا يُحفظ تلقائياً.
          </>
        )}
      </div>

      <PrintLayout title="معايير الاستحقاق" subtitle={`ساري بتاريخ ${formatDateLong(asOf)}`}>
        {isLoading ? (
          <p className="text-center text-sand-500 py-8">جاري التحميل...</p>
        ) : items.length === 0 ? (
          <p className="text-center text-sand-500 py-8">لا توجد أصناف مفعّلة</p>
        ) : (
          <div className="table-container overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th className="sticky right-0 bg-sand-50 z-10">المديرية / الفرع</th>
                  <th className="whitespace-nowrap">المحافظة</th>
                  {items.map((it) => <th key={it.id} className="text-center whitespace-nowrap">{it.nameAr}</th>)}
                </tr>
              </thead>
              <tbody>
                {visible.map((d) => (
                  <tr key={d.id}>
                    <td className="font-medium sticky right-0 bg-white border-l border-sand-200">{d.nameAr}</td>
                    <td className="text-sm text-sand-600 whitespace-nowrap">{d.governorate?.nameAr ?? "—"}</td>
                    {items.map((it) => (
                      <td key={it.id} className="text-center p-1">{cell(d, it.id)}</td>
                    ))}
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={items.length + 2} className="text-center text-sand-500 py-6">لا توجد مديريات مفعّلة</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </PrintLayout>
    </div>
  );
}
